import React, { useState, useRef, useEffect } from "react";
import api from "../api";

const errorMessage = (err) =>
  err?.response?.data?.error || err?.message || "Something went wrong";

export const emptyCourseDraft = {
  courseNumber: "",
  courseCardTitle: "",
  courseAuthor: "",
  courseCategory: "",
  pdfUrl: "",
  courseDescription: "",
  courseAuthorBio: "",
  courseReferenceDescription: "",
  courseGoals: [],
  courseIsActive: false,
  courseIsNew: false,
  isQuizInStudyMode: false,
  coverConcept: "",
};

export const draftFromCourse = (course) => ({
  _id: course?._id != null ? String(course._id) : course?._id,
  courseTitle: course?.courseTitle || "",
  courseNumber: course?.courseNumber || "",
  courseCardTitle: course?.courseCardTitle || "",
  courseAuthor: course?.courseAuthor || "",
  courseCategory: course?.courseCategory || "",
  pdfUrl: course?.pdfUrl || "",
  courseDescription: course?.courseDescription || "",
  courseAuthorBio: course?.courseAuthorBio || "",
  courseReferenceDescription: course?.courseReferenceDescription || "",
  courseGoals: Array.isArray(course?.courseGoals) ? [...course.courseGoals] : [],
  courseIsActive: Boolean(course?.courseIsActive),
  courseIsNew: Boolean(course?.courseIsNew),
  isQuizInStudyMode: Boolean(course?.isQuizInStudyMode),
  coverConcept: course?.coverConcept || "",
});

export const savePayloadFromDraft = (draft) => ({
  courseNumber: draft.courseNumber,
  courseCardTitle: draft.courseCardTitle,
  courseAuthor: draft.courseAuthor,
  pdfUrl: draft.pdfUrl,
  courseDescription: draft.courseDescription,
  courseAuthorBio: draft.courseAuthorBio,
  courseReferenceDescription: draft.courseReferenceDescription,
  courseGoals: draft.courseGoals,
});

export const CourseDataAgent = ({
  courseDraft,
  onDraftUpdate,
  selectedCourse,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const activeRunIdRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isThinking || !courseDraft?._id) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsThinking(true);

    const snapshot = JSON.parse(JSON.stringify(courseDraft));
    setUndoStack((prev) => [...prev, snapshot]);

    const runId = `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    activeRunIdRef.current = runId;

    try {
      const historySnapshot = messages.slice(-20).map((message) => ({
        role: message.role,
        content: message.content,
      }));

      const response = await api.post("/api/chat-course-edit", {
        runId,
        userMessage: text,
        chatHistory: historySnapshot,
        course: courseDraft,
      });

      const { updatedCourse, assistantMessage, stopped } = response.data;
      if (updatedCourse) onDraftUpdate(updatedCourse);

      setMessages((prev) => [
        ...prev,
        {
          role: stopped ? "system" : "assistant",
          content: assistantMessage || "Done.",
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${errorMessage(err)}`, isError: true },
      ]);
      setUndoStack((prev) => prev.slice(0, -1));
    } finally {
      activeRunIdRef.current = null;
      setIsThinking(false);
      setIsStopping(false);
    }
  };

  const handleStop = async () => {
    const runId = activeRunIdRef.current;
    if (!runId || isStopping) return;
    setIsStopping(true);
    try {
      await api.post("/api/chat-course-edit/stop", { runId });
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "system", content: "Could not reach the server to stop the run." },
      ]);
      setIsStopping(false);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    onDraftUpdate(last);
    setUndoStack((prev) => prev.slice(0, -1));
    setMessages((prev) => [
      ...prev,
      { role: "system", content: "↩ Reverted last agent change." },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isThinking) sendMessage();
    }
  };

  return (
    <div className="course-data-agent">
      <h3 className="course-workspace-heading">Course description</h3>
      <p className="course-workspace-hint">
        Ask the agent to rewrite the catalogue copy for {selectedCourse?.courseTitle || "this course"}.
        Changes stay in the editor until you save.
      </p>

      {!courseDraft?._id && (
        <div className="course-overview-loading">Loading course…</div>
      )}

      {undoStack.length > 0 && (
        <div className="agent-chat-context-bar course-data-agent-bar">
          <button
            className="agent-undo-button"
            onClick={handleUndo}
            disabled={isThinking}
            title={isThinking ? "Wait for the agent to finish or stop it first" : "Undo last AI change"}
          >
            ↩ Undo
          </button>
        </div>
      )}

      <div className="agent-chat-messages course-data-agent-messages">
        {messages.length === 0 && (
          <div className="agent-chat-empty">
            <p>Ask me to edit the course document.</p>
            <div className="agent-chat-examples">
              <button
                className="agent-example-chip"
                onClick={() => setInput("Regenerate the course description, goals, author bio and reference from the lessons")}
              >
                Regenerate catalogue copy
              </button>
              <button
                className="agent-example-chip"
                onClick={() => setInput("Make the description shorter and warmer")}
              >
                Shorter description
              </button>
              <button
                className="agent-example-chip"
                onClick={() => setInput("Rewrite the learning goals so they start with stronger verbs")}
              >
                Stronger goals
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`agent-message ${msg.role} ${msg.isError ? "error" : ""}`}
          >
            <div className="agent-message-role">
              {msg.role === "user" ? "You" : msg.role === "assistant" ? "Agent" : ""}
            </div>
            <div className="agent-message-content">
              {msg.content.split("\n").map((line, j) => (
                <span key={j}>
                  {line}
                  {j < msg.content.split("\n").length - 1 && <br />}
                </span>
              ))}
            </div>
          </div>
        ))}

        {isThinking && (
          <div className="agent-message assistant thinking">
            <div className="agent-message-role">Agent</div>
            <div className="agent-thinking-row">
              <div className="agent-thinking-dots">
                <span />
                <span />
                <span />
              </div>
              <button
                type="button"
                className="agent-chat-stop"
                onClick={handleStop}
                disabled={isStopping}
              >
                {isStopping ? "Stopping…" : "Stop"}
              </button>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="agent-chat-input-area">
        <textarea
          ref={inputRef}
          className="agent-chat-input"
          placeholder="e.g. 'Regenerate the catalogue copy', 'Shorten the description'…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          readOnly={isThinking}
        />
        {isThinking ? (
          <button
            type="button"
            className="agent-chat-stop"
            onClick={handleStop}
            disabled={isStopping}
          >
            {isStopping ? "Stopping…" : "■ Stop"}
          </button>
        ) : (
          <button
            type="button"
            className="agent-chat-send"
            onClick={sendMessage}
            disabled={!input.trim() || !courseDraft?._id}
          >
            Send
          </button>
        )}
      </div>
    </div>
  );
};

export const CourseDataPreview = ({
  courseDraft,
  saving,
  saveError,
  saveNotice,
  onSave,
  onDraftUpdate,
  importMode = false,
  onApprove,
  approving = false,
  approved = false,
}) => {
  if (!courseDraft) {
    return <div className="course-overview-loading">Loading course…</div>;
  }

  const setField = (key, value) => {
    if (onDraftUpdate) onDraftUpdate({ ...courseDraft, [key]: value });
  };

  const goals = courseDraft.courseGoals || [];

  return (
    <div className="course-data-preview">
      <div className="course-data-preview-header">
        <div>
          <div className="course-data-preview-kicker">
            {courseDraft.courseNumber ? `Course ${courseDraft.courseNumber}` : "Course"}
            {courseDraft.courseCategory ? ` · ${courseDraft.courseCategory}` : ""}
          </div>
          <h2 className="course-data-preview-title">
            {courseDraft.courseCardTitle || courseDraft.courseTitle || "Untitled course"}
          </h2>
          {courseDraft.courseAuthor && (
            <div className="course-data-preview-author">{courseDraft.courseAuthor}</div>
          )}
        </div>
        <div className="course-data-preview-actions">
          {!importMode && (
            <button className="save-lesson-button" onClick={onSave} disabled={saving || !courseDraft._id}>
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          {importMode && !approved && (
            <button
              className="bulk-btn bulk-btn-copy"
              onClick={onApprove}
              disabled={approving || saving || !courseDraft._id}
            >
              {approving ? "Approving…" : "✓ Approve Details"}
            </button>
          )}
        </div>
      </div>

      {saveError && <div className="generation-error">{saveError}</div>}
      {saveNotice && <div className="course-settings-notice">{saveNotice}</div>}

      <section>
        <div className="course-settings-label">Course number</div>
        <input
          className="course-settings-input"
          value={courseDraft.courseNumber || ""}
          onChange={(e) => setField("courseNumber", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">Card title</div>
        <input
          className="course-settings-input"
          value={courseDraft.courseCardTitle || ""}
          onChange={(e) => setField("courseCardTitle", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">Author</div>
        <input
          className="course-settings-input"
          value={courseDraft.courseAuthor || ""}
          onChange={(e) => setField("courseAuthor", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">PDF URL</div>
        <input
          className="course-settings-input"
          value={courseDraft.pdfUrl || ""}
          onChange={(e) => setField("pdfUrl", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">Description</div>
        <textarea
          className="course-settings-input"
          rows={5}
          value={courseDraft.courseDescription || ""}
          onChange={(e) => setField("courseDescription", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">Goals</div>
        {goals.map((goal, i) => (
          <div className="course-settings-goal-row" key={i}>
            <input
              className="course-settings-input"
              value={goal}
              onChange={(e) => {
                const next = [...goals];
                next[i] = e.target.value;
                setField("courseGoals", next);
              }}
            />
            <button
              type="button"
              className="course-settings-goal-remove"
              onClick={() => setField("courseGoals", goals.filter((_, index) => index !== i))}
              title="Remove goal"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="bulk-btn bulk-btn-secondary bulk-btn-sm"
          onClick={() => setField("courseGoals", [...goals, ""])}
        >
          + Add Goal
        </button>
      </section>

      <section>
        <div className="course-settings-label">Author bio</div>
        <textarea
          className="course-settings-input"
          rows={4}
          value={courseDraft.courseAuthorBio || ""}
          onChange={(e) => setField("courseAuthorBio", e.target.value)}
        />
      </section>

      <section>
        <div className="course-settings-label">Reference</div>
        <textarea
          className="course-settings-input"
          rows={3}
          value={courseDraft.courseReferenceDescription || ""}
          onChange={(e) => setField("courseReferenceDescription", e.target.value)}
        />
      </section>

      {courseDraft.coverConcept && (
        <section>
          <div className="course-settings-label">Cover concept</div>
          <p>{courseDraft.coverConcept}</p>
        </section>
      )}
    </div>
  );
};
